import { useProcessStartWizardWithCards } from '@frontend/utils/hooks/useProcessStartWizardWithCards';
import type { ICustomControlRequestReadProps } from '../../interfaces/customControlRequestReadProps';
import { getServiceLabel } from '../../../../../../common/customControls/getServiceLabel';
import { ProcessEntity } from '../../../../../../common/enums/processEntity';
import { getDisplayName } from '../../../../../../common/utils/getDisplayName';
import { translateKey } from '../../../../../translate';
import { getRequestReadButtons } from '../../../../elements/carePathButtons/RequestReadButtons';
import { ButtonContainer, FormDivider, UserTextParagraph } from '../../../../elements/common';
import { ContactWithRequesterOptionsRead } from '../../../../elements/ContactWithRequesterOptionsRead';
import { FormLabel } from '../../../../elements/forms/FormLabel';
import { HorizontalFormGroupRead } from '../../../../elements/forms/horizontalFormGroupRead/HorizontalFormGroupRead';
import { InputContainer } from '../../../../elements/forms/InputContainer';
import { ProcessContainer } from '../../../episodes/common';
import { ProcessItemFooter } from '../../../episodes/ProcessItemFooter';
import { ProcessItemHeader } from '../../../episodes/ProcessItemHeader';
import { ProfessionalSummaryDataRead } from '../../../professionalSummaryData/ProfessionalSummaryDataRead';
import { CustomControlCollapseBody } from '../../utils/v1/CustomControlCollapseBody';
import { DisplayWorkingDiagnosis } from '../../utils/v1/DisplayWorkingDiagnosis';
import { Icd10SelectionsRead } from '../../utils/v1/Icd10/Icd10SelectionsRead';
import { MedicationsRead } from '../../utils/v1/medications/MedicationsRead';
import { ProfileImages } from '../../utils/v1/ProfileImages';
import { RequestThumbnails } from '../../utils/v1/RequestThumbnails';
import { ServiceConsumerSupervisorRead } from '../../utils/v1/ServiceConsumerSupervisorRead';
import { TxcsMedicationsList } from '../../utils/v1/TxcsMedicationsList';
import { GgzcmdoV1FollowingSteps } from './GgzcmdoV1FollowingSteps';

export function GgzcmdoV1RequestRead(props: ICustomControlRequestReadProps) {
    const requestReadButtons = getRequestReadButtons(props);
    const hasProcessStartWizardWithCards = useProcessStartWizardWithCards();

    return (
        <ProcessContainer isActiveActivity={props.itemState.initialIsOpen}>
            <ProcessItemHeader
                date={props.process.dateSent || (props.request && props.request.dateSent)}
                isFirstItem={props.isFirstItem}
                isFirstProcess={props.itemState.isFirstProcess}
                isItemOpen={props.itemState.isOpen}
                onToggle={async (isOpened) => {
                    await props.updateItemState({
                        ...props.itemState,
                        isOpen: isOpened || !!props.itemState.initialIsOpen,
                    });
                }}
                organizationName={props.process.requesterOrganization?.name}
                practitionerName={
                    props.process.requesterPractitioner
                        ? getDisplayName(props.process.requesterPractitioner)
                        : undefined
                }
                process={props.process}
                serviceType={props.process.serviceType}
                title={getServiceLabel(props.process.code, 'request', props.process.name)}
            />

            <>
                <CustomControlCollapseBody isOpen={props.itemState.isOpen}>
                    <RequestThumbnails {...props} />
                    <ProfileImages
                        request={props.request}
                        showAlsoWhenNotRequested
                    />
                    <ServiceConsumerSupervisorRead {...props} />
                    <FormDivider />

                    <MedicationsRead
                        entity={ProcessEntity.request}
                        entityId={props.request.id}
                        patientUsesMedication={props.request.patientUsesMedication}
                    />
                    <ProfessionalSummaryDataRead request={props.request} />
                    <TxcsMedicationsList requestId={props.request.id} />

                    <DisplayWorkingDiagnosis
                        diagnosisConclusion={props.request.customControlData.conclusie_diagnose}
                        diagnosisConsiderationIcd10={props.request.diagnosis?.diagnosisConsiderationIcd10 || null}
                        thesaurusTermDate={props.request.dateSent}
                        thesaurusTermId={props.request.diagnosis?.considerationThesaurusTermId || null}
                    />

                    <Icd10SelectionsRead
                        diagnosis={props.request.diagnosis}
                        diagnosisConclusion={props.request.customControlData?.conclusie_diagnose}
                        hideTitle
                        thesaurusTermDate={props.request.dateSent}
                    />

                    {props.request.customControlData.toelichting && (
                        <HorizontalFormGroupRead>
                            <FormLabel>{translateKey('diagnosticReport.remarks')}</FormLabel>
                            <InputContainer>
                                <UserTextParagraph>{props.request.customControlData.toelichting}</UserTextParagraph>
                            </InputContainer>
                        </HorizontalFormGroupRead>
                    )}

                    {props.request.customControlData.vraag && (
                        <HorizontalFormGroupRead>
                            <FormLabel>{translateKey('request.question')}</FormLabel>
                            <InputContainer>
                                <UserTextParagraph>{props.request.customControlData.vraag}</UserTextParagraph>
                            </InputContainer>
                        </HorizontalFormGroupRead>
                    )}

                    <ContactWithRequesterOptionsRead {...props} />
                    <GgzcmdoV1FollowingSteps />
                    <ButtonContainer
                        alignRight
                        buttons={requestReadButtons}
                        noMarginTop={!hasProcessStartWizardWithCards}
                    />
                </CustomControlCollapseBody>

                {!props.itemState.initialIsOpen && (
                    <ProcessItemFooter
                        isItemOpen={props.itemState.isOpen}
                        labelLess={translateKey('request.hideConsult')}
                        labelMore={translateKey('request.showConsult')}
                        onToggle={async (isOpened) =>
                            await props.updateItemState({ ...props.itemState, isOpen: isOpened })
                        }
                    />
                )}
            </>
        </ProcessContainer>
    );
}
